/**
 * The emails a certification reimbursement claim sends as it moves.
 *
 * Their own file rather than more of emailNotification.ts, which is already past a thousand
 * lines and mixes six unrelated flows. The shared chrome — the wrapper, the button, the sender —
 * is imported from there, so these look like every other mail the product sends.
 *
 * ── Why these exist ───────────────────────────────────────────────────────────────────────
 *
 * The flow sent nothing at all. An employee raised a claim and their manager heard about it only
 * by opening the queue and noticing; a manager approved one and the certification admin was
 * never told; finance paid it and the employee found out by checking. Every other approval in
 * this product announces itself, and this one is now the same.
 *
 * ── Who each one goes to ──────────────────────────────────────────────────────────────────
 *
 * Whoever the claim is now waiting on, plus the employee whose money it is. The employee hears
 * about every step, because "it is still with the admin" and "it was rejected a week ago" look
 * identical from a screen that says nothing.
 */

import { APP_URL, actionButton, sendEmailSafe, wrapEmail } from './emailNotification';

/** Pending amber, matching the expense mails for the same state. */
const PENDING = '#f59e0b';
const APPROVED = '#0d8a6a';
const REJECTED = '#dc2626';

const rupees = (amount: number) => `₹${(Number(amount) || 0).toLocaleString()}`;

/** The detail table every one of these carries, so a reader sees the same facts each time. */
function claimFacts(input: {
  certificationName: string;
  provider?: string;
  result?: string;
  amount: number;
  employeeName?: string;
}): string {
  const row = (label: string, value: string, strong = false) => `
      <tr>
        <td style="color: #64748b; width: 140px; font-size: 14px;">${label}</td>
        <td style="color: ${strong ? '#0d8a6a' : '#1e293b'}; font-size: ${strong ? '18px' : '14px'}; font-weight: ${strong ? 700 : 600};">${value}</td>
      </tr>`;

  return `
    <table width="100%" cellpadding="12" cellspacing="0" style="background: #f8fafc; border-radius: 8px; margin: 20px 0;">
      ${row('Certification:', input.certificationName)}
      ${input.provider ? row('Provider:', input.provider) : ''}
      ${input.result ? row('Result:', input.result) : ''}
      ${input.employeeName ? row('Employee:', input.employeeName) : ''}
      ${row('Amount:', rupees(input.amount), true)}
    </table>`;
}

export interface ClaimEmailFacts {
  employeeName: string;
  certificationName: string;
  provider?: string;
  result?: string;
  amount: number;
}

/**
 * A new claim, to whoever must act on it first — the reporting manager, or the administrators
 * when the claim skips that stage.
 */
export async function emailCertificationReimbursementSubmitted(
  recipients: string[],
  input: ClaimEmailFacts & { reviewPath: string }
) {
  if (recipients.length === 0) return;
  const html = wrapEmail(
    '🎓 Certification Reimbursement Submitted',
    `
    <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
      <strong style="color: #111827;">${input.employeeName}</strong> has submitted a certification
      reimbursement claim for your review.
    </p>
    ${claimFacts(input)}
    ${actionButton('Review Claim', `${APP_URL}${input.reviewPath}`)}
  `,
    PENDING
  );
  await sendEmailSafe(
    recipients,
    `Certification claim: ${input.certificationName} - ${rupees(input.amount)}`,
    html
  );
}

/**
 * One stage said yes and the claim has moved on.
 *
 * Sent to the employee and to whoever now holds it, in one message, because the two need the
 * same facts and splitting them is how the wording drifts apart.
 */
export async function emailCertificationReimbursementApproved(
  recipients: string[],
  input: ClaimEmailFacts & {
    /** Who approved, in words — "Your manager", "The certification admin". */
    approvedBy: string;
    /** What happens next, in words. */
    nextStep: string;
    reviewPath: string;
  }
) {
  if (recipients.length === 0) return;
  const html = wrapEmail(
    '✅ Certification Claim Approved',
    `
    <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
      ${input.approvedBy} has approved the certification reimbursement claim from
      <strong style="color: #111827;">${input.employeeName}</strong>.
    </p>
    ${claimFacts(input)}
    <p style="color: #475569; font-size: 14px; line-height: 1.6; margin: 0 0 20px 0;">
      ${input.nextStep}
    </p>
    ${actionButton('View Claim', `${APP_URL}${input.reviewPath}`)}
  `,
    APPROVED
  );
  await sendEmailSafe(
    recipients,
    `Approved: ${input.certificationName} - ${rupees(input.amount)}`,
    html
  );
}

/**
 * Any stage said no. To the employee, with the reason — a rejection with no reason is the one
 * outcome nobody can act on, which is why the service refuses to record one without it.
 */
export async function emailCertificationReimbursementRejected(
  recipients: string[],
  input: ClaimEmailFacts & { rejectedBy: string; reason: string }
) {
  if (recipients.length === 0) return;
  const html = wrapEmail(
    '❌ Certification Claim Rejected',
    `
    <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
      ${input.rejectedBy} has rejected the certification reimbursement claim for
      <strong style="color: #111827;">${input.certificationName}</strong>.
    </p>
    ${claimFacts(input)}
    <table width="100%" cellpadding="12" cellspacing="0" style="background: #fef2f2; border-left: 4px solid ${REJECTED}; border-radius: 6px; margin: 20px 0;">
      <tr>
        <td style="color: #7f1d1d; font-size: 14px; line-height: 1.6;">
          <strong>Reason:</strong> ${input.reason}
        </td>
      </tr>
    </table>
    ${actionButton('View Claim', `${APP_URL}/certifications/reimbursement`)}
  `,
    REJECTED
  );
  await sendEmailSafe(
    recipients,
    `Rejected: ${input.certificationName} - ${rupees(input.amount)}`,
    html
  );
}

/** Finance has paid it. The end of the flow, and the message the employee is waiting for. */
export async function emailCertificationReimbursementReimbursed(
  recipients: string[],
  input: ClaimEmailFacts
) {
  if (recipients.length === 0) return;
  const html = wrapEmail(
    '💰 Certification Reimbursement Processed',
    `
    <p style="color: #1f2937; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
      The certification reimbursement for
      <strong style="color: #111827;">${input.certificationName}</strong> has been processed by
      finance.
    </p>
    ${claimFacts(input)}
    ${actionButton('View Claim', `${APP_URL}/certifications/reimbursement`)}
  `,
    APPROVED
  );
  await sendEmailSafe(
    recipients,
    `Reimbursed: ${input.certificationName} - ${rupees(input.amount)}`,
    html
  );
}
